import {ChangeDetectorRef, Component, DestroyRef, EventEmitter, OnInit, Output, inject} from '@angular/core';
import {filter, Observable, of, shareReplay, tap} from 'rxjs';
import {
  dispatchDeletedItemEvent,
  dispatchSavedItemEvent,
  SearchCriteria,
  SearchResult
} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  MenuInterface,
  VpbAangifte,
  BokkzVpbDataService,
  BokkzVpbLogic,
  VpbBerekening,
  FeatureServiceComponent,
  BokkzAlgDataService,
  BokkzClient,
  CorporateTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule, NgStyle} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-corporate-list',
  templateUrl: './feature-tax-corporate-list.component.html',
  styleUrls: ['./feature-tax-corporate-list.component.scss'],
  imports: [
    CommonModule,
    SharedUiTableModule,
    SharedUiButtonModule,
    NgStyle,
    FeatureServiceComponent
  ]
})
export class FeatureTaxCorporateListComponent implements OnInit {

  @Output() public details = new EventEmitter<string>();
  private readonly destroyRef = inject(DestroyRef);
  public readonly dataService = inject(BokkzVpbDataService);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  public readonly vpbService = inject(BokkzVpbLogic);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected tableConfig!: TableConfiguration<any, any>;
  protected clientId = '';

  public showTable = false;
  public year = 0;
  public options: MenuInterface[] = [];
  private visibleMenuSnapshot: MenuInterface[] = [];
  public corporateTaxEntity = {} as CorporateTaxEntity;
  public berekening = {} as VpbBerekening;
  public aangifteVorigJaar = {} as VpbAangifte;
  public berekeningVorigJaar = {} as VpbBerekening;
  public winstCorrecties = 0;
  public winstCorrectiesVorigJaar = 0;
  public saldoFiscaleWinstberekening = 0;
  public belastbaarBedrag = 0;
  public verschuldigdeVennootschapsbelasting: number | string = 0;
  public verschuldigdeVennootschapsbelastingVorigjaar: number | string = 0;
  public belastbaarBedragVorigjaar: number | string = '-';
  public saldoFiscaleWinstberekeningVorigjaar: number | string = '-';
  public isDefAangifte = false;
  public naamVennootschap = '';
  public dialogVisible = false;
  public activePage = '';
  private dispatchMenuTimer: ReturnType<typeof setTimeout> | null = null;

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(entity => {
        if (entity.aangifte) {
          this.corporateTaxEntity = entity;
          this.naamVennootschap = entity.aangifte?.identificatie?.naamLichaam ?? '';
          this.dataService.updateMenuInterface(entity.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly isDefAangifte$: Observable<boolean> =
    this.dataService.isDefAangifte.pipe(
      tap(isDefAangifte => {
        this.isDefAangifte = isDefAangifte;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening.pipe(
      filter((data): data is VpbBerekening => this.hasResolvedVpbBerekening(data)),
      tap(value => {
        this.winstCorrecties = (value.belastbareWinst?.saldoFiscaleWinst || 0) - (value.belastbareWinst?.belastbareWinst || 0);
        this.belastbaarBedrag = value.belastbaarBedrag?.belastbaarBedrag || 0;
        this.saldoFiscaleWinstberekening = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening || 0;
        this.verschuldigdeVennootschapsbelasting = value.belasting?.verschuldigdeVennootschapsbelasting || 0;
        this.berekening = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbAangifteVergelijking$: Observable<VpbAangifte> =
    this.dataService.vpbAangifteVergelijking.pipe(
      filter((data): data is VpbAangifte => !!data),
      tap(value => {
        this.aangifteVorigJaar = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekeningVergelijking$: Observable<VpbBerekening> =
    this.dataService.vpbBerekeningVergelijking.pipe(
      filter((data): data is VpbBerekening => this.hasResolvedVpbBerekening(data)),
      tap(value => {
        this.berekeningVorigJaar = value;
        const fiscaleWinstBerekeningVorigJaar = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening || 0;
        this.belastbaarBedragVorigjaar = value.belastbaarBedrag?.belastbaarBedrag || '-';
        this.winstCorrectiesVorigJaar = fiscaleWinstBerekeningVorigJaar - (value.belastbaarBedrag?.belastbaarBedrag || 0);
        this.saldoFiscaleWinstberekeningVorigjaar = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening?.toLocaleString() || '-';
        this.verschuldigdeVennootschapsbelastingVorigjaar = value.belasting?.verschuldigdeVennootschapsbelasting?.toLocaleString() || '-';
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      if (this.dispatchMenuTimer) {
        clearTimeout(this.dispatchMenuTimer);
        this.dispatchMenuTimer = null;
      }
    });

    this.corporateTaxEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.isDefAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vpbBerekening$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vpbAangifteVergelijking$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vpbBerekeningVergelijking$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.observeContext();
    this.observeMenuInterface();
  }

  private observeContext(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.clientId = params['clientId'] ?? '';
      this.loadPageData();
    });

    this.algDataService.selectedYear.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(year => {
      this.year = year;
      this.loadPageData();
    });
  }

  private observeMenuInterface(): void {
    this.dataService.menuInterface.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(menuInterface => {
      const previousVisibleMenu = this.visibleMenuSnapshot;
      const next = this.sortVisibleMenu(menuInterface);
      const hasVisibleMenuChanged = !this.areMenuListsEqual(this.visibleMenuSnapshot, next);

      if (hasVisibleMenuChanged) {
        this.options = this.cloneVisibleMenu(next);
        this.visibleMenuSnapshot = this.cloneVisibleMenu(next);
        this.cd.detectChanges();

        if (this.showTable) {
          this.scheduleVisibleMenuDispatch(previousVisibleMenu, this.visibleMenuSnapshot);
        }
      }
    });
  }

  private loadPageData(): void {
    if (!this.clientId || !this.year) {
      return;
    }

    if (this.dispatchMenuTimer) {
      clearTimeout(this.dispatchMenuTimer);
      this.dispatchMenuTimer = null;
    }

    this.showTable = false;
    this.options = [];
    this.visibleMenuSnapshot = [];

    this.bokkzClient.getCorporateTax(this.clientId, String(this.year)).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: corporateTax => {
        this.dataService.updateCorporateTaxEntity(corporateTax);

        if (corporateTax.aangifte) {
          this.dataService.calculateLatestCorporateTaxReturn().pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe({
            next: (berekening) => {
              this.applyBerekening(berekening);
              this.finishPageLoad();
            },
            error: () => {
              this.finishPageLoad();
            }
          });
          return;
        }

        this.dataService.updateAantalValidaties(0);
        this.finishPageLoad();
      },
      error: () => {
        this.options = [];
        this.dataService.updateAantalValidaties(0);
        this.finishPageLoad();
      }
    });
  }

  private finishPageLoad(): void {
    this.createTableConfig();
    queueMicrotask(() => {
      this.showTable = true;
    });
  }

  private scheduleVisibleMenuDispatch(previous: MenuInterface[], current: MenuInterface[]): void {
    if (this.dispatchMenuTimer) {
      clearTimeout(this.dispatchMenuTimer);
    }

    const previousSnapshot = this.cloneVisibleMenu(previous);
    const currentSnapshot = this.cloneVisibleMenu(current);

    this.dispatchMenuTimer = setTimeout(() => {
      this.dispatchVisibleMenuChanges(previousSnapshot, currentSnapshot);
      this.dispatchMenuTimer = null;
    }, 25);
  }

  private dispatchVisibleMenuChanges(previous: MenuInterface[], current: MenuInterface[]): void {
    // First remove the currently rendered rows.
    previous.forEach(item => {
      dispatchDeletedItemEvent({identifier: item.value});
    });

    // Then add the sorted rows on the next tick so the table processes a true replace.
    setTimeout(() => {
      [...current].reverse().forEach(item => {
        dispatchSavedItemEvent({
          identifier: item.value,
          result: item as unknown as Record<number, MenuInterface>
        });
      });
    }, 0);
  }

  private sortVisibleMenu(options: MenuInterface[]): MenuInterface[] {
    return [...options]
      .filter(item => item.visible)
      .map(group => ({
        ...group,
        subonderdelen: (group.subonderdelen || [])
          .filter(sub => sub.visible)
          .sort((a, b) => a.value - b.value)
          .map(sub => ({...sub}))
      }))
      .sort((a, b) => a.value - b.value);
  }

  private cloneVisibleMenu(options: MenuInterface[]): MenuInterface[] {
    return options.map(group => ({
      ...group,
      subonderdelen: (group.subonderdelen || []).map(sub => ({...sub}))
    }));
  }


  private areMenuListsEqual(left: MenuInterface[], right: MenuInterface[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => this.areMenuItemsEqual(item, right[index]));
  }

  private areMenuItemsEqual(left?: MenuInterface, right?: MenuInterface): boolean {
    if (!left || !right) {
      return left === right;
    }

    return left.value === right.value &&
      left.id === right.id &&
      left.label === right.label &&
      left.visible === right.visible &&
      left.pageMapping === right.pageMapping &&
      this.areSubOnderdelenEqual(left.subonderdelen, right.subonderdelen);
  }

  private areSubOnderdelenEqual(left?: MenuInterface['subonderdelen'], right?: MenuInterface['subonderdelen']): boolean {
    if (!left?.length && !right?.length) {
      return true;
    }

    if (!left || !right || left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => {
      const other = right[index];
      return item.label === other.label &&
        item.pageMapping === other.pageMapping &&
        item.visible === other.visible &&
        item.value === other.value;
    });
  }

  private hasResolvedVpbBerekening(data: VpbBerekening | null | undefined): data is VpbBerekening {
    return !!(data?.jaarstukken || data?.belasting || data?.belastbaarBedrag || data?.belastbareWinst);
  }

  private applyBerekening(value: VpbBerekening): void {
    this.winstCorrecties = (value.belastbareWinst?.saldoFiscaleWinst || 0) - (value.belastbareWinst?.belastbareWinst || 0);
    this.belastbaarBedrag = value.belastbaarBedrag?.belastbaarBedrag || 0;
    this.saldoFiscaleWinstberekening = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening || 0;
    this.verschuldigdeVennootschapsbelasting = value.belasting?.verschuldigdeVennootschapsbelasting || 0;
    this.berekening = value;
  }

  private createTableConfig(): void {
    const criteria = new SearchCriteria();

    this.tableConfig = TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true
      },
      criteria,

      search: () => of(new SearchResult(this.options, this.options.length)),

      details: (entity) => {
        if (entity) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024
          ? 'Module niet beschikbaar'
          : $localize`Geen aangifte gevonden voor ` + this.year,
        subText: this.year < 2024
          ? 'De aangifte vennootschapsbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
          : 'Maak een nieuwe aangifte aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe aangifte`,
        buttonConfig: this.year < 2024 ? undefined : ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                subscriber.next((this.dialogVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
      }),
    });
  }

  public navigateToSubPage(pageMapping: string): void {
    const url = '/tax-corporate/' + 'modules/' + this.clientId + '/' + pageMapping;
    this.router.navigate([url]).then(() => console.log('complete'));
    this.activePage = pageMapping;
  }
}
